import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Slide,
  Typography,
} from '@mui/material'
import {
  Close as CloseIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'

export interface IncomingChatData {
  sessionId: string
  notificationId: string
  customerName?: string
  customerEmail?: string
  widgetName?: string
  reason?: string
  createdAt: number
  expiresAt: number
}

interface IncomingChatNotificationProps {
  notification: IncomingChatData
  onAccept: (sessionId: string) => void
  onDismiss: (notificationId: string) => void
}

export const IncomingChatNotification = ({
  notification,
  onAccept,
  onDismiss,
}: IncomingChatNotificationProps) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0)
  const [accepting, setAccepting] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const startAlert = () => {
    if (!audioRef.current) {
      // Create audio element with notification sound
      audioRef.current = new Audio('/notification-sound.mp3')
      audioRef.current.loop = true
      audioRef.current.volume = 0.5
    }

    audioRef.current.play().catch((err) => {
      console.warn('Failed to play notification sound:', err)
    })
  }

  const stopAlert = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
    }
  }

  useEffect(() => {
    // Calculate initial time remaining
    const updateTimeRemaining = () => {
      const remaining = Math.max(0, notification.expiresAt - Date.now())
      setTimeRemaining(remaining)

      if (remaining === 0) {
        stopAlert()
      }
    }

    updateTimeRemaining()
    const interval = setInterval(updateTimeRemaining, 1000)

    return () => clearInterval(interval)
  }, [notification.expiresAt])

  useEffect(() => {
    // Start audio alert
    startAlert()

    return () => {
      stopAlert()
    }
  }, [])

  const handleAccept = () => {
    if (accepting) return
    setAccepting(true)
    stopAlert()
    onAccept(notification.sessionId)
  }

  const handleDismiss = () => {
    stopAlert()
    onDismiss(notification.notificationId)
  }

  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const getReasonText = () => {
    if (notification.reason?.includes('AI') || notification.reason?.includes('transfer')) {
      return 'AI Transfer'
    }
    return 'New Chat'
  }

  return (
    <Slide direction="left" in={true} mountOnEnter unmountOnExit>
      <Card
        sx={{
          position: 'fixed',
          top: 80,
          right: 24,
          width: 400,
          zIndex: 9999,
          boxShadow: 6,
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%, 100%': {
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)',
            },
            '50%': {
              boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)',
            },
          },
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Alert severity="warning" icon={false} sx={{ py: 0, px: 1 }}>
                <AlertTitle sx={{ mb: 0, fontSize: '0.875rem', fontWeight: 600 }}>
                  {getReasonText()}
                </AlertTitle>
              </Alert>
              <Chip
                icon={<TimeIcon />}
                label={formatTime(timeRemaining)}
                color={timeRemaining < 30000 ? 'error' : 'warning'}
                size="small"
              />
            </Box>
            <IconButton size="small" onClick={handleDismiss} aria-label="Dismiss notification">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box mb={2}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <PersonIcon color="action" fontSize="small" />
              <Typography variant="body1" fontWeight={600}>
                {notification.customerName || 'Anonymous Visitor'}
              </Typography>
            </Box>

            {notification.customerEmail && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                {notification.customerEmail}
              </Typography>
            )}

            {notification.widgetName && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                Widget: {notification.widgetName}
              </Typography>
            )}

            {notification.reason && (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 1 }}>
                {notification.reason}
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block', mt: 0.5 }}>
              {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
            </Typography>
          </Box>

          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAccept}
              disabled={accepting}
              sx={{ fontWeight: 600 }}
            >
              {accepting ? 'Accepting...' : 'Accept Chat'}
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleDismiss}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              Dismiss
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Slide>
  )
}
